import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Badge, Box, List, ListItemButton, ListItemIcon } from '@mui/material';
import { CheckLoginComponent } from 'App';
import { CommonLoggedInDataWithToken } from 'CommonLoggedInData';
import { InvestorContextType } from 'InvestorContext';
import React from 'react';
import { Location, NavigateFunction } from 'react-router-dom';
import type { ActionsContextType } from '../types/ActionsContextType';
import { SidebarToneContext } from './helpers/SidebarContext';
import SidebarItem from './Sidebar/SidebarItem';
import { Palette } from 'Palette';
import { StyledDrawer } from './styles/styles';

interface AbstractSidebarProps {
  context: CommonLoggedInDataWithToken;
  location: Location;
  navigate: NavigateFunction;
  tone?: keyof typeof Palette;
  mobile?: boolean; // <-- new
  investorContext?: InvestorContextType;
  actionsCount?: number;
  actionsContext?: ActionsContextType;
  hasAdvisorAccess?: boolean;
  isAdvisorViewActive?: boolean;
}

interface AbstractSidebarState {
  expandDueToChildren: boolean;
  mobileOpen: boolean;
}

abstract class AbstractSidebarBase extends React.Component<
  AbstractSidebarProps,
  AbstractSidebarState
> {
  constructor(props: AbstractSidebarProps) {
    super(props);
    this.state = {
      expandDueToChildren: false,
      mobileOpen: false,
    };

    props.context.sidebarExpanded = false;
  }

  toggleMobileMenu() {
    this.setState((prev) => ({ mobileOpen: !prev.mobileOpen }));
  }

  handleHoverIntent = (hasChildren: boolean) => {
    this.setState({ expandDueToChildren: hasChildren && false });
    this.props.context.sidebarExpanded = hasChildren && false;
  };

  closeSidebar = () => {
    this.setState({ expandDueToChildren: false });
    this.props.context.sidebarExpanded = false;
  };

  abstract sidebarItems(): ReturnType<typeof SidebarItem>[];

  bottomSidebarItems(): ReturnType<typeof SidebarItem>[] {
    return [];
  }

  renderItems() {
    const regularItems = this.sidebarItems();
    const bottomItems = this.bottomSidebarItems();

    return (
      <>
        {regularItems.map((item: any, index: number) => {
          if (this.props.mobile) {
            return (
              <SidebarItem
                key={`mobile-${index}`}
                {...item.props}
                forMobile={true}
                onClick={() => this.toggleMobileMenu()}
              />
            );
          }

          return React.cloneElement(item, { key: `sidebar-${index}` });
        })}
        {bottomItems.length > 0 && !this.props.mobile && <Box sx={{ flexGrow: 1 }} />}
        {bottomItems.map((item: any, index: number) => {
          if (this.props.mobile) {
            return (
              <SidebarItem
                key={`mobile-bottom-${index}`}
                {...item.props}
                forMobile={true}
                onClick={() => this.toggleMobileMenu()}
              />
            );
          }

          return React.cloneElement(item, { key: `sidebar-bottom-${index}` });
        })}
      </>
    );
  }

  renderMobileMenuButton(tone: keyof typeof Palette) {
    const { mobileOpen } = this.state;
    return (
      <ListItemButton
        disableGutters
        onClick={() => this.toggleMobileMenu()}
        className="sidebar-hamburger"
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        sx={{
          width: '100%',
          color: Palette['tone1'].text,
          px: 0,
          justifyContent: 'flex-end',
          '& .MuiListItemIcon-root': {
            justifyContent: 'flex-end',
            minWidth: 0,
            color: tone ? Palette[tone].text : 'inherit',
          },
          '& svg': {
            fontSize: '3rem',
            color: Palette['tone1'].text,
          },
          '& .MuiListItemButton-root': {
            color: Palette['tone1'].text,
          },
          display: { md: 'none' },
        }}
      >
        <ListItemIcon>
          {mobileOpen ? (
            <CloseIcon />
          ) : (
            <Badge
              badgeContent={this.props.actionsCount}
              color="info"
              invisible={!this.props.actionsCount}
              sx={{
                '& .MuiBadge-badge': {
                  right: -3,
                  top: 3,
                  fontSize: '0.65rem',
                  minWidth: '16px',
                  height: '16px',
                },
              }}
            >
              <MenuIcon />
            </Badge>
          )}
        </ListItemIcon>
      </ListItemButton>
    );
  }

  renderMobileMenuOverlay() {
    const { mobileOpen } = this.state;
    if (!mobileOpen) {
      return null;
    }

    return (
      <Box
        data-capture-region="sidebar"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100vw',
          maxWidth: 'calc(100vw - 2rem)',
          height: 'auto',
          maxHeight: '100vh',
          overflow: 'hidden',
          background: '#fff',
          zIndex: 1300,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          transform: 'translateX(0%)',
          transformOrigin: '206px 0px',
          opacity: 1,
          borderRadius: '1.667rem',
          transitionProperty: 'max-height, transform, opacity, padding, gap',
          transitionDuration: '0.3s, 0.3s, 0.3s, 0.3s, 0.3s',
          transitionTimingFunction: 'ease',
          transitionDelay: '0s',
          mt: '6.67rem',
          pointerEvents: 'auto',
          boxShadow: 3,
        }}
      >
        <List>{this.renderItems()}</List>
      </Box>
    );
  }

  renderDesktopSidebar(tone: keyof typeof Palette) {
    const { expandDueToChildren } = this.state;
    return (
      <StyledDrawer
        tone={tone}
        className="hide-print react-Sidebar"
        data-capture-region="sidebar"
        variant="permanent"
        anchor="left"
        expanded={expandDueToChildren}
        onMouseLeave={this.closeSidebar}
        sx={{
          color: Palette['tone1'].text,
          pl: 0,
          pr: 0,
          position: 'fixed',
          height: 'calc(100% - 80px)',
          overflowX: 'visible',
          zIndex: (theme) => theme.zIndex.appBar - 1,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {this.renderItems()}
        </List>
      </StyledDrawer>
    );
  }

  render() {
    const { tone = 'tone4', mobile } = this.props;

    return (
      <CheckLoginComponent notLoggedInChildren={<></>}>
        <SidebarToneContext.Provider value={tone}>
          <>
            {mobile && (
              <>
                {this.renderMobileMenuButton(tone)}
                {this.renderMobileMenuOverlay()}
              </>
            )}

            {!mobile && this.renderDesktopSidebar(tone)}
          </>
        </SidebarToneContext.Provider>
      </CheckLoginComponent>
    );
  }
}

export default AbstractSidebarBase;
